import { Alert } from '@ophelos/ui/components/alert';
import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Card } from '@ophelos/ui/components/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@ophelos/ui/components/input-otp';
import { Loader } from '@ophelos/ui/components/loader';
import { Loader as LoaderStyled } from '@/components/ui/Loader';
import { AlertCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import { OphelosIcon } from '@/components/branding/ophelos-icon';
import { Layout } from '@/components/layout/Layout';
import { useTheme } from '@/components/layout/ThemeProvider';
import { SettingsMenu } from '@/components/ui/SettingsMenu';
import { useAuth } from '@/contexts/customer-auth-context';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useTranslation } from '@/hooks/useTranslation';
import { type AuthSessionDto, getAuthSessionFromConnect, logPortalActivityToConnect } from '@/lib/connect-api';
import { persistLimitedSession } from '@/lib/customer-session';
import { bindGlobalCellFromCode } from '@/lib/global/resolve-code';
import { REDIRECT_PARAM, safeRedirectTarget } from '@/lib/redirect';
import { isGlobalSurface } from '@/lib/surface-mode';

export default function Login() {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTarget = safeRedirectTarget(searchParams.get(REDIRECT_PARAM));
  const { config, isDemoMode, isPreviewMode, isLoading: configLoading, loadAccountConfigAndReturn } = useConfiguration();
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const otpContainerRef = useRef<HTMLDivElement>(null);

  // Redirect authenticated users away from login page
  useEffect(() => {
    // Don't redirect in preview mode, while config is loading, or if not authenticated
    if (isPreviewMode || configLoading || !isAuthenticated) return;

    // Stage-2 magic-auth / claim is removed — continue as Stage-1. Honour an
    // operator-sent payment deep link (?redirect=) when present, else dashboard.
    navigate(redirectTarget ?? '/dashboard', { replace: true });
  }, [isAuthenticated, isPreviewMode, configLoading, navigate, redirectTarget]);

  // Auto-focus OTP input on page load
  useEffect(() => {
    if (otpContainerRef.current) {
      const hiddenInput = otpContainerRef.current.querySelector<HTMLInputElement>('input');
      if (hiddenInput) {
        setTimeout(() => hiddenInput.focus(), 100);
      }
    }
  }, []);

  // Refocus the OTP input when an error occurs to maintain focus for backspace
  useEffect(() => {
    if (error && otpContainerRef.current) {
      const hiddenInput = otpContainerRef.current.querySelector<HTMLInputElement>('input');
      if (hiddenInput) {
        setTimeout(() => hiddenInput.focus(), 0);
      }
    }
  }, [error]);

  // Get logo URL based on current theme (light/dark)
  const logo = config.ui.branding.logo;
  const logoUrl = theme === 'dark' ? logo?.dark : logo?.light;

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);

    if (code.length !== 6) {
      setError(t('login.pleaseEnterCode'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Demo mode: Bypass access code validation but respect auth configuration
      if (isDemoMode) {
        const mockAccountId = `demo_account_${code}`;
        localStorage.setItem(STORAGE_KEYS.ACCESS_CODE, code);
        localStorage.setItem(STORAGE_KEYS.AUTH_STAGE, 'limited');
        localStorage.setItem(STORAGE_KEYS.ACCOUNT_ID, mockAccountId);
        localStorage.setItem(STORAGE_KEYS.CUSTOMER_NAME, 'Demo Customer');
        localStorage.setItem(STORAGE_KEYS.DEMO_MODE, 'true');

        // Persist auth mode for page reload scenarios (prevents flash of protected content)
        const authMode = config.authentication?.authMode || 'none';
        localStorage.setItem(STORAGE_KEYS.AUTH_MODE, authMode);

        // Brief delay to give sense of security processing
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Stage-2 magic-auth / claim is removed — Stage-1 continues to dashboard.
        navigate(redirectTarget ?? '/dashboard');

        // Dispatch custom event to notify auth context of localStorage update (same-tab)
        window.dispatchEvent(new Event('localStorageAuthUpdate'));
        return;
      }

      // Normal mode: Validate access code via Connect API
      if (isGlobalSurface()) {
        try {
          await bindGlobalCellFromCode(code);
        } catch {
          setError(t('login.invalidAccessCode'));
          setIsLoading(false);
          return;
        }
      }

      let session: AuthSessionDto;
      try {
        session = await getAuthSessionFromConnect(code);
      } catch {
        setError(t('login.invalidAccessCode'));
        setIsLoading(false);
        return;
      }

      await persistLimitedSession(code, session, loadAccountConfigAndReturn);

      // Log portal activity (fire-and-forget, but log errors)
      logPortalActivityToConnect(session.account_id, 'login', {}, 'limited').catch((error) => {
        console.error('Failed to log portal activity:', error);
      });

      // Stage-2 magic-auth / claim is removed — Stage-1 continues to dashboard.
      navigate(redirectTarget ?? '/dashboard');
    } catch (err) {
      console.error('Unexpected error during authentication:', err);
      setError(t('login.unexpectedError'));
      setIsLoading(false);
    }
  };

  const isInvalid = hasAttemptedSubmit && code.length !== 6;

  const handleCodeChange = (value: string) => {
    setCode(value.toUpperCase());
    // Clear validation state when user starts typing
    if (hasAttemptedSubmit && value.length > code.length) {
      setHasAttemptedSubmit(false);
      setError(null);
    }
  };

  // Show loader while redirect is pending or checking auth
  if (!isPreviewMode && isAuthenticated) {
    return <Loader fullScreen />;
  }

  return (
    <Layout>
      <div className="relative w-full max-w-5xl mx-auto">
        <SettingsMenu className="absolute top-4 max-md:left-4 md:right-4 z-20" />
        <div className="w-full max-w-lg mx-auto px-4 space-y-4 relative z-10 mt-[8vh] md:mt-[12vh]">
          <div className="text-center stack gap-8">
            {logoUrl ? (
              <img src={logoUrl} alt={logo?.alt || 'Logo'} className="w-auto max-w-32 h-8 mx-auto object-contain" />
            ) : (
              <OphelosIcon className="w-11 h-11 mx-auto text-muted-foreground/25" />
            )}
            <div className="px-4">
              <h1 className="text-4xl font-medium text-balance">{t('login.greeting')}</h1>
            </div>
          </div>

          <Card
            variant="fullscreen"
            className="gap-6 px-4 xs:px-9 theme-professional:shadow-xs theme-professional:mt-6 theme-professional:py-8 theme-approachable:py-5 theme-approachable:bg-transparent theme-approachable:border-transparent theme-approachable:shadow-none"
          >
            <div className="flex flex-col gap-4 relative">
              <p className="text-lg w-full text-center">{t('login.enterAccessCode')}</p>

              <div className="relative flex justify-center w-full" ref={otpContainerRef}>
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={handleCodeChange}
                  onComplete={handleSubmit}
                  variant="solo"
                  className="w-full"
                  disabled={isLoading}
                  pattern="^[a-zA-Z0-9]*$"
                  inputMode="text"
                >
                  <InputOTPGroup className="w-full">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot key={index} index={index} className="h-16 text-xl" aria-invalid={isInvalid} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>

                {isLoading && (
                  <Badge
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-muted border-border-heavy gap-2"
                    variant="outline"
                  >
                    <LoaderStyled size="sm" />
                    <span>{t('login.loggingIn')}</span>
                  </Badge>
                )}
              </div>
            </div>

            <Button size="xl" onClick={handleSubmit} className="w-full">
              {isLoading ? <Loader size="sm" colour="primary" /> : t('login.accessYourAccount')}
            </Button>

            {error && (
              <Alert variant="destructive" className="flex rounded-lg justify-center">
                <div className="flex items-center gap-2">
                  <AlertCircle className="size-4 text-destructive" />
                  {error}
                </div>
              </Alert>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
